import themeColors from '@layers-frontend/commons/styles/themeColors'
import { createTheme } from '@mui/material'
import { css } from 'styled-components'

export const UIStyles = themeColors

// Surface recipes shared by the panels that sit over the map.
export const surfaces = {
  // Docked panels touch the bars: mint contour on the free right edge and a shadow cast on the map.
  dockedPanelShadow: `1px 0 0 0 ${themeColors.mintBorder}, 16px 0 32px -16px ${themeColors.blackTransparent06}`,
  // The same panel docked at the right edge (the LAYI chat aside): the contour follows the free left edge.
  dockedRightPanelShadow: `-1px 0 0 0 ${themeColors.mintBorder}, -16px 0 32px -16px ${themeColors.blackTransparent06}`,
  // Docked cards that end above the screen bottom: the contour follows the right and bottom edges.
  dockedCardShadow: `1px 1px 0 0 ${themeColors.mintBorder}, 12px 12px 32px -12px ${themeColors.blackTransparent06}`,
  // Menus docked under the top bar at the right edge (user menu, ☰ menu): the contour follows the left and bottom edges.
  dockedRightCardShadow: `-1px 1px 0 0 ${themeColors.mintBorder}, -12px 12px 32px -12px ${themeColors.blackTransparent06}`,
  // Floating popovers (dropdowns, menus) cast a soft shadow below them.
  floatingPanelShadow: `0 12px 32px -8px ${themeColors.blackTransparent06}`,
  // Panels floating over the map on their own: the mint contour sits inside the box so it keeps its size.
  mapPanelShadow: `inset 0 0 0 1px ${themeColors.mintBorder}, 0 12px 32px -8px ${themeColors.blackTransparent06}`,
  // Raised round controls over the map (floating action buttons, the comparison swipe's buttons) cast a tighter shadow.
  raisedControlShadow: `0 6px 16px ${themeColors.blackTransparent035}`,
  // Modals sit above everything, so they cast the deepest shadow.
  modalShadow: `0 24px 64px -16px ${themeColors.blackTransparent06}`,
  // Frosted glass: the map behind a translucent panel blurs instead of competing with its text.
  glassBackdropFilter: 'blur(12px) saturate(1.3)'
}

// Select triggers inside panels and modals (sx on StyledSelect; on a StyledTextInput, InputProps sx): mintBorder underline,
// mint on hover and while open or typing.
export const panelSelectSx = {
  '&::before': {
    borderBottomColor: themeColors.mintBorder
  },
  '&:hover:not(.Mui-disabled, .Mui-error)::before': {
    borderBottom: `1px solid ${themeColors.mintColor}`
  },
  '&::after': {
    borderBottomColor: themeColors.mintColor
  },
  '& .MuiSelect-icon': {
    color: themeColors.whiteTransparent08
  },
  '& .MuiSelect-iconOpen': {
    color: themeColors.mintColor
  }
}

// The same underline for the MUI standard fields of a form in a panel or a modal, as class rules for its styled wrapper:
// mintBorder underline, mint on hover and while typing, and a mint focused label. Error states keep MUI's red.
export const panelFieldRules = css`
  .MuiInput-underline:not(.Mui-error)::before {
    border-bottom-color: ${themeColors.mintBorder};
  }
  .MuiInput-underline:hover:not(.Mui-disabled):not(.Mui-error)::before {
    border-bottom: 1px solid ${themeColors.mintColor};
  }
  .MuiInput-underline:not(.Mui-error)::after {
    border-bottom-color: ${themeColors.mintColor};
  }
  label.Mui-focused:not(.Mui-error) {
    color: ${themeColors.mintColor};
  }
`

// Dropdown papers (menus, select lists, autocomplete lists) open as solid navy popovers with the mint contour, like
// the tooltips: glass would let the rows behind show through.
const dropdownPaperStyles = {
  backgroundColor: themeColors.deepblueColor3,
  borderRadius: '12px',
  boxShadow: surfaces.mapPanelShadow
}

// Their scroll areas keep only the mint thumb: the global scrollbar paints a blueColor rail.
const dropdownScrollbarStyles = {
  '&::-webkit-scrollbar, &::-webkit-scrollbar-track': {
    backgroundColor: themeColors.transparent,
    boxShadow: 'none'
  }
}

// Options read soft white, light up under the pointer or keyboard focus, and the selected one is marked like a
// selected row: mint tint and a mint bar on the left.
const dropdownOptionHighlight = {
  background: themeColors.whiteTransparent01,
  color: themeColors.whiteColor
}

const dropdownOptionSelected = {
  background: themeColors.mintColorTransparent,
  boxShadow: `inset 4px 0 0 ${themeColors.mintColor}`,
  color: themeColors.whiteColor
}

export const paletteTheme = {
  primary: {
    light: UIStyles.mintColor,
    main: UIStyles.whiteColor,
    dark: UIStyles.mintColor
  },
  secondary: {
    main: themeColors.grayColor
  },
  text: {
    primary: UIStyles.whiteColor,
    secondary: UIStyles.whiteColor,
    disabled: themeColors.grayColor
  }
}

export const typographyTheme = {
  fontSize: 20
}

export const baseThemeComponents = {
  MuiInput: {
    styleOverrides: {
      root: {
        '::before': {
          borderBottomColor: UIStyles.grayColor,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid'
        },
        fontSize: 15
      },
      input: {
        '::placeholder': {
          color: UIStyles.grayColor,
          opacity: 1
        }
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: UIStyles.grayColor,
        fontSize: 15
      }
    },
    defaultProps: {
      color: 'secondary'
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      popper: {
        zIndex: 1600,
        maxHeight: 500
      },
      paper: dropdownPaperStyles,
      // MUI keeps the option states on the listbox, so the colours go there too: this override is inserted after
      // them and wins the tie. The option override is applied through the popper and keeps only the sizes.
      listbox: {
        ...dropdownScrollbarStyles,
        '& .MuiAutocomplete-option': {
          color: UIStyles.whiteTransparent08,
          '&.Mui-focused, &.Mui-focusVisible': dropdownOptionHighlight,
          '&[aria-selected="true"], &[aria-selected="true"].Mui-focused, &[aria-selected="true"].Mui-focusVisible': dropdownOptionSelected
        }
      },
      option: {
        fontSize: 15,
        paddingBottom: 10,
        paddingTop: 10
      },
      noOptions: {
        color: UIStyles.whiteTransparent06,
        fontSize: 15
      },
      loading: {
        color: UIStyles.whiteTransparent06
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        color: UIStyles.whiteColor,
        height: 30,
        fontSize: 14
      },
      deleteIcon: {
        color: UIStyles.redColor,
        width: 18,
        '&:hover': {
          color: UIStyles.redColor
        }
      },
      avatar: {
        color: UIStyles.whiteColor,
        width: 30,
        height: 30,
        marginLeft: 0,
        fontSize: 14
      }
    }
  },
  MuiDialog: {
    styleOverrides: {
      // Modals float like the dropdowns: deep navy, mint contour, 16px corners. The contour is an outline so content
      // painted up to the edges cannot cover it.
      paper: {
        background: UIStyles.deepblueColor3,
        maxWidth: '35%',
        borderRadius: '16px',
        boxShadow: surfaces.modalShadow,
        outline: `1px solid ${UIStyles.mintBorder}`,
        outlineOffset: '-1px'
      }
    }
  },
  MuiDatePicker: {
    styleOverrides: {
      root: {
        color: UIStyles.mintColor,
        fontSize: '1.2rem'
      },
      contentWrapper: {
        background: 'transparent',
        '& .MuiInputAdornment ': {
          color: themeColors.whiteColor
        },
        '&  .MuiButtonBase-root-MuiIconButton-root': {
          color: themeColors.whiteColor
        }
      }
    }
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: UIStyles.white,
        fontSize: 13
      }
    }
  },
  MuiPickersPopper: {
    styleOverrides: {
      // Calendars that open from a date field float like the dropdowns. The field panel's calendar has no popper and
      // keeps its look. The extra layout class outranks the shared MuiPickersLayout rules below.
      paper: {
        ...dropdownPaperStyles,
        '& .MuiPickersLayout-root': {
          // The layout's own navy fill gives way to the paper, so the mint contour shows.
          '& .MuiPickersLayout-contentWrapper': {
            backgroundColor: UIStyles.transparent
          },
          '& .MuiPickersDay-root:not(.Mui-selected):hover': {
            backgroundColor: UIStyles.whiteTransparent01
          },
          // The selected day and year are solid mint like the other selected pills, so the day reads apart from
          // today's white ring. The transparent border keeps the day's size.
          '& .MuiPickersDay-root.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected': {
            backgroundColor: UIStyles.mintColor,
            borderColor: UIStyles.transparent,
            color: UIStyles.deepblueColor3,
            '&:hover, &:focus': {
              backgroundColor: UIStyles.mintColor
            }
          }
        }
      }
    }
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: 12,
        position: 'absolute',
        bottom: '-20px',
        color: UIStyles.redColor
      }
    }
  },
  MuiSwitch: {
    styleOverrides: {
      track: {
        background: UIStyles.whiteColor
      }
    }
  },
  MuiIcon: {
    styleOverrides: {
      root: {
        width: 30
      }
    }
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: UIStyles.grayColor
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      // The navy of the map buttons with the mint contour of the panels. The text is 12px like the map's measurement
      // labels: MUI's default rem size renders at about 9.8px on this app's 10px root.
      tooltip: {
        background: UIStyles.deepblueColor3,
        borderRadius: '8px',
        boxShadow: surfaces.mapPanelShadow,
        fontSize: 12
      },
      arrow: {
        color: UIStyles.deepblueColor3
      }
    }
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        padding: '10px 0',
        justifyContent: 'space-between',
        marginTop: 5,
        color: UIStyles.mintColor
      }
    }
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '3rem',
        color: 'white',
        textAlign: 'center'
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      icon: {
        color: UIStyles.grayColor,
        fontSize: 25
      },
      root: {
        '&.Mui-disabled': {
          color: UIStyles.grayColor
        }
      }
    }
  },
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
      InputLabelProps: {
        shrink: true
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        ...dropdownPaperStyles,
        ...dropdownScrollbarStyles
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      // MUI's own selected + keyboard-focus tint is white at 20%, so every selected state is listed here.
      root: {
        color: UIStyles.whiteTransparent08,
        '&:hover, &.Mui-focusVisible': dropdownOptionHighlight,
        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': dropdownOptionSelected
      }
    }
  },
  // Toasts are pills that float over the map: deep navy with the mint contour, or solid mint, red or amber for the
  // success, error and alert classes of actions/notifications.js.
  MuiSnackbarContent: {
    styleOverrides: {
      action: {
        margin: '0 auto'
      },
      root: {
        justifyContent: 'center',
        fontSize: 15,
        backgroundColor: themeColors.deepblueColor3,
        color: themeColors.whiteColor,
        borderRadius: '999px',
        boxShadow: `inset 0 0 0 1px ${themeColors.mintBorder}, ${surfaces.floatingPanelShadow}`,
        '&.SUCCESS_NOTIFICATION_CLASS': {
          backgroundColor: themeColors.mintColor,
          color: themeColors.deepblueColor3,
          boxShadow: surfaces.floatingPanelShadow
        },
        '&.ERROR_NOTIFICATION_CLASS': {
          backgroundColor: themeColors.redColor,
          color: themeColors.whiteColor,
          boxShadow: surfaces.floatingPanelShadow
        },
        '&.ALERT_NOTIFICATION_CLASS': {
          backgroundColor: themeColors.vividOrange2,
          color: themeColors.deepblueColor3,
          boxShadow: surfaces.floatingPanelShadow
        }
      }
    }
  },
  // Spinners take the mint accent instead of the white primary.
  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: themeColors.mintColor
      }
    }
  },
  MuiPickersLayout: {
    styleOverrides: {
      contentWrapper: {
        // background: 'rgba(8,44,63,.8)',
        color: themeColors.whiteColor,
        '& .MuiIconButton-root': {
          color: themeColors.grayColor
        },

        '& .MuiIconButton-root:hover': {
          background: themeColors.darkGrayHoverColor
        },

        '& .MuiPickersCalendarHeader-label': {
          color: themeColors.whiteColor,
          fontSize: 17.5
        },

        '& .MuiCalendarPicker-viewTransitionContainer': {
          overflow: 'hidden'
        },

        '& .MuiCalendarPicker-viewTransitionContainer:has(.MuiYearPicker-root)': {
          overflowY: 'auto'
        },

        '& .MuiPickersMonth-monthButton.Mui-disabled': { color: themeColors.grayColor },

        '& .MuiPickersYear-yearButton.Mui-disabled': { color: themeColors.grayColor },

        '& .MuiTypography-root': {
          color: themeColors.grayColor,
          fontSize: '13px'
        },

        '& .MuiButton-text': {
          color: themeColors.whiteColor,
          background: 'none',
          width: '36px',
          minWidth: '36px',
          height: '36px',
          borderRadius: '50%',
          margin: '0 2px',
          pointerEvents: 'none'
        },

        '& .MuiButton-root:disabled': {
          color: themeColors.grayColor
        },

        '& .Mui-withSources': {
          background: themeColors.customLightOverlayBackground,
          cursor: 'pointer',
          pointerEvents: 'auto'
        },

        '& .Mui-withSources:hover': {
          background: themeColors.whiteTransparent01,
          cursor: 'pointer'
        },

        '& .Mui-selected, & .Mui-selected:hover': {
          background: themeColors.customLightOverlayBackground,
          color: themeColors.whiteColor,
          border: `1px solid ${themeColors.whiteColor}`
        },

        '& .PrivatePickersYear-root .Mui-selected, .MuiMonthPicker-root .Mui-selected, & .PrivatePickersYear-root .Mui-selected:hover, .MuiMonthPicker-root .Mui-selected:hover': {
          color: themeColors.whiteColor,
          background: themeColors.lightBlue
        },

        '& .MuiTypography-root:disabled, .PrivatePickersYear-root .Mui-disabled': {
          color: themeColors.lightGrey,
          background: themeColors.transparent
        },

        '& .MuiTypography-root:disabled:hover, .PrivatePickersYear-root .Mui-disabled:hover': {
          background: themeColors.transparent
        },

        '& .MuiPickersYear-yearButton:hover, & .MuiPickersMonth-monthButton:hover': {
          background: themeColors.whiteTransparent01
        },

        '& .MuiPickersYear-yearButton.Mui-selected:hover, & .MuiPickersMonth-monthButton.Mui-selected:hover': {
          background: themeColors.whiteTransparent01
        },

        '& .PrivatePickersSlideTransition-root .Mui-disabled': {
          color: themeColors.lightGrey
        }
      },
      root: {
        color: themeColors.whiteColor,
        '& .MuiPickersLayout-contentWrapper': {
          backgroundColor: themeColors.blueColor
        },
        '& .MuiPickersLayout-toolbar': {
          display: 'none'
        },
        '& .MuiDialogActions-root': {
          background: 'red',
          display: 'none'
        },
        background: 'transparent'
      }
    }
  }
}

export const baseTheme = createTheme({
  palette: paletteTheme,
  typography: typographyTheme,
  components: baseThemeComponents
})
